import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { navigationItems } from '@/config/navigationConfig';
import { useAuth } from '@/contexts/AuthContext';
import UserMenu from '@/components/layout/UserMenu';
import NotificationCenter from '@/components/layout/NotificationCenter';

// Surface — fixed frost, independent of wallpaper luminance.
// backdropFilter is injected via CSS var (--sidebar-backdrop-filter) because
// the brightness() component flips with the theme to scrim hot regions.
const SIDEBAR_GLASS = {
  bg: 'rgba(255, 255, 255, 0.1)',
  outerBorder: '1px solid rgba(255, 255, 255, 0.15)',
  backdropFilter: 'var(--sidebar-backdrop-filter)',
};

// Foreground — flips with the wallpaper luminance via --sidebar-fg.
const SIDEBAR_COLORS = {
  text: 'var(--sidebar-fg)',
  textMuted: 'color-mix(in srgb, var(--sidebar-fg) 60%, transparent)',
  hover: 'color-mix(in srgb, var(--sidebar-fg) 10%, transparent)',
  active: 'color-mix(in srgb, var(--sidebar-fg) 20%, transparent)',
  border: 'color-mix(in srgb, var(--sidebar-fg) 12%, transparent)',
};

interface NavItemProps {
  item: Record<string, any>;
  currentView: string;
  currentSubview?: string | null;
  onNavigate: (...args: any[]) => void;
  isExpanded: boolean;
  depth?: number;
  expandedItems: Record<string, boolean>;
  onToggleExpand: (id: string) => void;
}

function NavItem({ item, currentView, currentSubview, onNavigate, isExpanded, depth = 0, expandedItems, onToggleExpand }: NavItemProps) {
  const { hasPermission } = useAuth();

  const isActive = item.groupView
    ? (currentView === item.groupView ||
       (item.groupView === 'obras' && currentView === 'obras-dashboard'))
    : (item.view && currentView === item.view &&
       item.subview && currentSubview === item.subview);
  const hasChildren = item.children && item.children.length > 0;
  const Icon = item.icon;
  const isItemExpanded = expandedItems[item.id] || false;

  // Check permissions
  if (item.permission && !hasPermission(item.permission)) {
    return null;
  }
  if (!item.permission && hasChildren) {
    const hasAnyVisibleChild = item.children.some((child: any) =>
      !child.permission || hasPermission(child.permission)
    );
    if (!hasAnyVisibleChild) {
      return null;
    }
  }

  // Determine if there are visible children (for chevron and expand)
  const hasVisibleChildren = hasChildren && item.children.some((child: any) =>
    !child.permission || hasPermission(child.permission)
  );

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (hasChildren && item.groupView) {
      onNavigate(item.groupView, item.subview);
    } else if (hasChildren) {
      onToggleExpand(item.id);
    } else if (item.view) {
      onNavigate(item.view, item.subview);
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand(item.id);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        style={{
          width: '100%',
          padding: isExpanded ? '10px 16px' : '10px 12px',
          border: 'none',
          background: isActive ? SIDEBAR_COLORS.active : 'transparent',
          color: isActive ? SIDEBAR_COLORS.text : 'color-mix(in srgb, var(--sidebar-fg) 85%, transparent)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: isExpanded ? '12px' : '0',
          justifyContent: isExpanded ? 'flex-start' : 'center',
          borderRadius: '8px',
          margin: '2px 0',
          fontSize: depth > 0 ? '13px' : '14px',
          fontWeight: isActive ? '600' : '500',
          transition: 'all 0.2s ease',
          paddingLeft: isExpanded ? `${16 + (depth * 20)}px` : '12px',
          textAlign: 'left'
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = SIDEBAR_COLORS.hover;
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
        title={!isExpanded ? item.label : ''}
      >
        <Icon size={18} style={{ flexShrink: 0 }} />

        {isExpanded && (
          <>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              {item.label}
            </span>
            {hasVisibleChildren && (
              <div
                onClick={handleChevronClick}
                style={{
                  flexShrink: 0,
                  padding: '4px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'color-mix(in srgb, var(--sidebar-fg) 15%, transparent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {isItemExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </div>
            )}
          </>
        )}
      </button>

      {hasVisibleChildren && isItemExpanded && isExpanded && (
        <div style={{ marginLeft: '8px' }}>
          {item.children.map((child: any) => (
            <NavItem
              key={child.id}
              item={child}
              currentView={currentView}
              currentSubview={currentSubview}
              onNavigate={onNavigate}
              isExpanded={isExpanded}
              depth={depth + 1}
              expandedItems={expandedItems}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </>
  );
}

interface SideNavigationProps {
  isExpanded: boolean;
  isPinned: boolean;
  onTogglePin: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  currentView: string;
  currentSubview?: string | null;
  onNavigate: (...args: any[]) => void;
  onOpenDrawer?: (type: string, id: string) => void;
  isMobile: boolean;
}

export default function SideNavigation({
  isExpanded,
  isPinned,
  onTogglePin,
  onMouseEnter,
  onMouseLeave,
  currentView,
  currentSubview,
  onNavigate,
  onOpenDrawer,
  isMobile
}: SideNavigationProps) {
  const { loading: authLoading, permissionCache, hasPermission } = useAuth();

  const isLoadingPermissions = authLoading || Object.keys(permissionCache).length === 0;
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  // Auto-expand parents of active items
  useEffect(() => {
    const newExpandedItems: Record<string, boolean> = {};

    const findAndExpandParent = (items: any[], parentId: string | null = null) => {
      for (const item of items) {
        const isActive = item.view && currentView === item.view &&
          item.subview && currentSubview === item.subview;

        if (isActive && parentId) {
          newExpandedItems[parentId] = true;
        }

        if (item.children) {
          const hasActiveChild = item.children.some((child: any) =>
            child.view && currentView === child.view &&
            child.subview && currentSubview === child.subview
          );

          if (hasActiveChild) {
            newExpandedItems[item.id] = true;
          }

          findAndExpandParent(item.children, item.id);
        }
      }
    };

    findAndExpandParent(navigationItems);

    setExpandedItems(prevExpanded => ({
      ...prevExpanded,
      ...newExpandedItems
    }));
  }, [currentView, currentSubview]);

  const handleToggleExpand = (itemId: string) => {
    setExpandedItems(prev => {
      const newExpanded = { ...prev };
      if (newExpanded[itemId]) {
        delete newExpanded[itemId];
      } else {
        newExpanded[itemId] = true;
      }
      return newExpanded;
    });
  };

  const handleNavigation = (view: string, subview?: string) => {
    onNavigate(view, subview);
  };

  // Helper function to check if item or its children are visible
  const isItemVisible = (item: any): boolean => {
    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }
    // Item has its own permission and passes — visible even without visible children
    if (item.permission) {
      return true;
    }
    // No own permission: group visibility depends on children
    if (item.children && item.children.length > 0) {
      return item.children.some((child: any) => isItemVisible(child));
    }
    return true;
  };

  // Agrupar items por categoria
  const mainItems = navigationItems
    .filter(item => item.category === 'main' || !item.category)
    .filter(item => isItemVisible(item));

  const toolItems = navigationItems
    .filter(item => item.category === 'tools')
    .filter(item => isItemVisible(item));

  const adminItems = navigationItems
    .filter(item => item.category === 'admin')
    .filter(item => isItemVisible(item));

  // Loading state
  if (isLoadingPermissions) {
    return (
      <aside
        className="side-navigation"
        style={{
          position: 'fixed',
          top: '10px',
          left: '10px',
          bottom: '10px',
          width: isExpanded ? '280px' : '64px',
          background: SIDEBAR_GLASS.bg,
          backdropFilter: SIDEBAR_GLASS.backdropFilter,
          WebkitBackdropFilter: SIDEBAR_GLASS.backdropFilter,
          border: SIDEBAR_GLASS.outerBorder,
          color: SIDEBAR_COLORS.text,
          textShadow: 'var(--sidebar-text-shadow)',
          borderRadius: '20px',
          transition: 'width 0.3s ease',
          zIndex: 999,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{ color: SIDEBAR_COLORS.textMuted, fontSize: '14px' }}>
          {isExpanded ? 'Carregando...' : '...'}
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="side-navigation"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed',
        top: '10px',
        left: '10px',
        bottom: '10px',
        width: isExpanded ? '280px' : '64px',
        background: SIDEBAR_GLASS.bg,
        backdropFilter: SIDEBAR_GLASS.backdropFilter,
        WebkitBackdropFilter: SIDEBAR_GLASS.backdropFilter,
        border: SIDEBAR_GLASS.outerBorder,
        color: SIDEBAR_COLORS.text,
        textShadow: 'var(--sidebar-text-shadow)',
        borderRadius: '20px',
        transition:
          'width 0.3s ease, box-shadow 0.3s ease, color 0.25s ease, text-shadow 0.25s ease, backdrop-filter 0.25s ease, -webkit-backdrop-filter 0.25s ease',
        zIndex: 999,
        overflowY: 'hidden',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        // Sombra projetada para a direita
        boxShadow: '4px 0 20px rgba(0, 0, 0, 0.12)'
      }}
    >
      {/* Header - Logo e Pin */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '89px',
          paddingTop: '20px',
          paddingBottom: '16px',
          paddingLeft: '8px',
          paddingRight: '8px',
          borderBottom: `1px solid ${SIDEBAR_COLORS.border}`,
          flexShrink: 0
        }}
      >
        {/* Logo - clicável, navega para Home.
            Uses the PNG as a mask so the glyph color follows --sidebar-fg. */}
        <span
          role="img"
          aria-label="Joshua"
          onClick={() => onNavigate('home')}
          style={{
            display: 'block',
            height: '57px',
            width: isExpanded ? '196px' : '64px',
            minWidth: isExpanded ? '196px' : '64px',
            maxWidth: isExpanded ? '196px' : '64px',
            marginLeft: '-8px',
            flexShrink: 0,
            cursor: 'pointer',
            backgroundColor: SIDEBAR_COLORS.text,
            WebkitMaskImage: `url(/${isExpanded ? 'logo_joshua_menu_lateral_expandido.png' : 'logo_joshua_menu_lateral_recolhido.png'})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'left center',
            maskImage: `url(/${isExpanded ? 'logo_joshua_menu_lateral_expandido.png' : 'logo_joshua_menu_lateral_recolhido.png'})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'left center',
            filter: 'var(--sidebar-logo-shadow)',
            transition: 'width 0.3s ease'
          }}
        />

        {/* Pin - só aparece quando expandido e não é mobile */}
        {isExpanded && !isMobile && (
          <button
            className={`btn-pin pin-animated${isPinned ? ' pinned' : ''}`}
            onClick={onTogglePin}
            title={isPinned ? 'Desafixar menu' : 'Fixar menu'}
            style={{ color: SIDEBAR_COLORS.text, filter: 'var(--sidebar-logo-shadow)' }}
          >
            {isPinned ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <g className="pin-body">
                  <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1V2H8v4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
                </g>
                <g className="pin-needle">
                  <path d="M12 17v5"/>
                </g>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <g className="pin-body">
                  <path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V9"/>
                  <path d="M15 6V2H8v4h1"/>
                </g>
                <g className="pin-needle">
                  <path d="M12 17v5"/>
                </g>
                <g className="pin-slash">
                  <path d="M2 2l20 20"/>
                </g>
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Navegação principal */}
      <nav
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '12px 8px'
        }}
      >
        {/* Items principais */}
        {mainItems.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            currentView={currentView}
            currentSubview={currentSubview}
            onNavigate={handleNavigation}
            isExpanded={isExpanded}
            expandedItems={expandedItems}
            onToggleExpand={handleToggleExpand}
          />
        ))}

        {toolItems.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            currentView={currentView}
            currentSubview={currentSubview}
            onNavigate={handleNavigation}
            isExpanded={isExpanded}
            expandedItems={expandedItems}
            onToggleExpand={handleToggleExpand}
          />
        ))}

        {adminItems.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            currentView={currentView}
            currentSubview={currentSubview}
            onNavigate={handleNavigation}
            isExpanded={isExpanded}
            expandedItems={expandedItems}
            onToggleExpand={handleToggleExpand}
          />
        ))}
      </nav>

      {/* Footer - Notificações e User Menu */}
      <div
        style={{
          borderTop: `1px solid ${SIDEBAR_COLORS.border}`,
          padding: isExpanded ? '12px' : '12px 8px',
          flexShrink: 0,
          position: 'relative',
          zIndex: 1001, // Acima do sidebar
          display: 'flex',
          flexDirection: isExpanded ? 'row' : 'column',
          alignItems: 'center',
          gap: isExpanded ? '8px' : '12px'
        }}
      >
        <NotificationCenter isExpanded={isExpanded} onOpenDrawer={onOpenDrawer} />
        <div style={{ flex: isExpanded ? 1 : 'none' }}>
          <UserMenu isExpanded={isExpanded} />
        </div>
      </div>
    </aside>
  );
}
